import React, { useRef, useState, useCallback } from 'react';
import { TableCard } from '../TableCard/TableCard';
import type { FloorPlanProps, TablePosition } from './types';

// ─── FloorPlan ────────────────────────────────────────────────────────────────

export function FloorPlan({
  tables,
  onTableClick,
  onTableMove,
  editable = false,
  className,
}: FloorPlanProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (tableId: string, e: React.MouseEvent<HTMLDivElement>) => {
      if (!editable) return;
      e.preventDefault();
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      setDragging(tableId);
    },
    [editable],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragging || !canvasRef.current) return;
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - canvasRect.left - dragOffset.current.x;
      const y = e.clientY - canvasRect.top  - dragOffset.current.y;
      onTableMove?.(dragging, {
        x: Math.max(0, x),
        y: Math.max(0, y),
      });
    },
    [dragging, onTableMove],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  return (
    <div
      ref={canvasRef}
      className={[
        'relative w-full bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden',
        'select-none',
        editable ? 'cursor-default' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ minHeight: 480 }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {tables.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center text-neutral-400 dark:text-neutral-500">
          <p className="text-sm font-medium">No tables configured</p>
        </div>
      ) : (
        tables.map(({ position, ...tableProps }) => (
          <div
            key={tableProps.id}
            onMouseDown={(e) => handleMouseDown(tableProps.id, e)}
            style={{
              position: 'absolute',
              left: position.x,
              top:  position.y,
              width:  position.width,
              height: position.height,
              cursor: editable ? 'grab' : 'pointer',
              zIndex: dragging === tableProps.id ? 10 : 1,
            }}
          >
            <TableCard
              {...tableProps}
              onClick={onTableClick}
            />
          </div>
        ))
      )}

      {/* Edit mode indicator */}
      {editable && (
        <div className="absolute top-2 right-2 px-2 py-1 rounded bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 text-xs font-medium">
          Edit mode — drag to reposition
        </div>
      )}
    </div>
  );
}
